const TermsAndConditionsContentListItem = ({ children }: { children: any }) => {
  return <li className="pb-4">{children}</li>;
};

export const TermsAndConditionsContent = () => {
  return (
    <div>
      <h2 className="text-center">OTP Mobile App - Terms of Use</h2>
      <p>
        <strong>PLEASE READ THE FOLLOWING TERMS OF USE CAREFULLY.</strong>
      </p>

      <p>
        These Terms of Use apply to your use of the OTP mobile application (the
        <b>“OTP Mobile App”</b> or “the <b>app</b>”) as provided by McDonald’s
        USA, LLC (“<b>McDonald’s</b>”). Use of the OTP Mobile App is optional
        and is provided as an alternative method to access the applications you
        would normally access via a McDonald’s desktop or tablet in the
        restaurant from a mobile device.
      </p>

      <p>
        By accessing or using the OTP Mobile App, you agree to be bound by these
        Terms of Use. If you do not agree, do not use the OTP Mobile App. Your
        use of and authorization to access the OTP Mobile App and the materials
        and content available therein is subject to your compliance with these
        Terms of Use.
      </p>

      <p>
        McDonald’s reserves the right to change, revise, or amend these Terms of
        Use at any time without prior notice. Your continued use of the OTP
        Mobile App after any such changes constitutes your acceptance of the new
        Terms of Use and agreement to abide by them. You should review these
        Terms of Use regularly to stay informed of any changes that may affect
        you.
      </p>

      <p>
        <b>Please note that:</b>

        <ul>
          <TermsAndConditionsContentListItem>
            The Terms of Use that apply to employees of restaurants owned and
            operated by McDonald’s franchisees (independent Owner-Operators) are
            contained in Section A below.
          </TermsAndConditionsContentListItem>
          <TermsAndConditionsContentListItem>
            The Terms of Use that apply to employees of McDonald’s Corporation,
            McDonald’s USA, LLC, and McDonald’s corporate-owned restaurants are
            contained in Section B below.
          </TermsAndConditionsContentListItem>
        </ul>
      </p>

      <h3>
        <b>
          A. TERMS OF USE APPLICABLE TO EMPLOYEES OF RESTAURANTS OWNED AND
          OPERATED BY INDEPENDENT OWNER-OPERATORS ONLY
        </b>
      </h3>
      <ol>
        <TermsAndConditionsContentListItem>
          McDonald’s Corporation, McDonald’s USA, and their subsidiaries and/or
          affiliates are not your employer and have no control over employment
          matters at restaurants owned and operated by its franchisees. Your
          employer is a McDonald’s-branded restaurant owned and operated by an
          independent Owner-Operator, who is solely responsible for all
          employment matters in your restaurant, including setting and enforcing
          policies and practices regarding wages, hours, and working conditions,
          including your use of the OTP Mobile App. Please check with the person
          designated by your employer to manage these matters (for example, your
          General Manager) to determine the policies and practices used in your
          employer’s organization regarding the OTP Mobile App, including
          policies regarding use of the OTP Mobile App when you are not clocked
          in.
        </TermsAndConditionsContentListItem>
        <TermsAndConditionsContentListItem>
          Access to the OTP Mobile App is authorized only for the individual
          associated with each applicable username and password. The OTP Mobile
          App may only be used while you are and remain employed by your present
          employer. If this employment ends, your access to the OTP Mobile App
          will terminate.
        </TermsAndConditionsContentListItem>
        <TermsAndConditionsContentListItem>
          The OTP Mobile App may not be used by you for any purpose that is not
          authorized by and/or consistent with these Terms of Use.
        </TermsAndConditionsContentListItem>
        <TermsAndConditionsContentListItem>
          McDonald’s may monitor and record your use of the OTP Mobile App,
          including activity you conduct while using the app, to operate the OTP
          Mobile App, to evaluate your use thereof, for compliance and audit
          purposes, and to protect against fraud, illegal activity, violation of
          McDonald’s policies, or misuse of the OTP Mobile App or McDonald’s
          information, assets, or other property. Accordingly, you should not
          have any expectation of privacy in connection with your use of the OTP
          Mobile App.
        </TermsAndConditionsContentListItem>
        <TermsAndConditionsContentListItem>
          Your employer may restrict, suspend, or terminate your access to the
          OTP Mobile App for any reason not prohibited by law. Failure to comply
          with these Terms of Use, policies applicable to your employment, and
          applicable laws may subject you to discipline, up to and including
          termination from employment, and may also result in the restriction,
          suspension, or termination of your access to the OTP Mobile App. You
          understand and agree that your employer may take any one or more of
          these actions without providing notice to you, except as prohibited by
          law.
        </TermsAndConditionsContentListItem>
        <TermsAndConditionsContentListItem>
          McDonald’s and/or its subsidiaries and/or affiliates are not liable
          for any damages or injury arising out of or related to the OTP Mobile
          App, however caused, including, but not limited to, any failure of
          performance, error, omission, interruption, delay, or defect caused as
          a direct or indirect result of you using the OTP Mobile App.
        </TermsAndConditionsContentListItem>
        <TermsAndConditionsContentListItem>
          Any communication or material you transmit through the OTP Mobile App,
          including any data, posts, comments, remarks, suggestions, ideas,
          graphics, photographs, materials, or other information (collectively,
          the “<b>Submissions</b>”) is, and will be treated as, non-confidential
          and non-proprietary. McDonald’s may contact you about any such
          Submission(s) for business- related purposes. Furthermore, you grant
          McDonald’s the perpetual, irrevocable, royalty-free, nonexclusive, and
          worldwide right to use the Submission(s) for any purpose whatsoever,
          commercial or otherwise, including, but not limited to, developing,
          manufacturing, and marketing products or services, or conducting data
          analytics, using such Submissions, without your further approval and
          without providing you any further notice or compensation. By making
          any Submissions, you represent that you have all requisite rights to,
          and are authorized to disclose, all of the information contained in
          the Submissions.
        </TermsAndConditionsContentListItem>
        <TermsAndConditionsContentListItem>
          Any and all rights in the OTP Mobile App are and shall remain the
          exclusive property of McDonald’s or its licensors. For purposes of
          clarity, the OTP Mobile App includes any and all content on the OTP
          Mobile App, such as, but not limited to, text, images, graphics,
          logos, page headers, button icons, images, audio clips, digital
          downloads, data compilations, software, trademarks, service marks,
          trade dress, audio, video, data and other materials as well as any
          part of the OTP Mobile App. The OTP Mobile App is licensed, not sold,
          to you. Nothing in these terms intends to transfer any such rights to,
          or to vest any such right in, you. You may not take any action to
          jeopardize, limit or interfere with McDonald’s or its licensor’s
          rights.
        </TermsAndConditionsContentListItem>
        <TermsAndConditionsContentListItem>
          Trademarks, service marks, and all graphical elements, including the
          look and feel appearing in the OTP Mobile App, are distinctive and
          protected trademarks or trade dress of McDonald’s or its licensors.
          The OTP Mobile App may also contain various third-party names,
          trademarks, and service marks that are the property of their
          respective owners.
        </TermsAndConditionsContentListItem>
        <TermsAndConditionsContentListItem>
          Any questions regarding the OTP Mobile App should be directed to Atos
          at (800) 515-3636.
        </TermsAndConditionsContentListItem>
        <TermsAndConditionsContentListItem>
          Any exclusion or restriction of liability contained in these Terms of
          Use shall apply to the fullest extent permitted by applicable law.
        </TermsAndConditionsContentListItem>
        <TermsAndConditionsContentListItem>
          Any use of the OTP Mobile App shall be subject to the applicable laws
          of the State of Illinois, and any dispute arising from such use or
          otherwise in connection with the OTP Mobile App shall be subject to
          the exclusive jurisdiction of the federal or state courts in the State
          of Illinois, as applicable.
        </TermsAndConditionsContentListItem>
      </ol>

      <h3>
        <b>
          B. TERMS OF USE APPLICABLE TO EMPLOYEES OF MCDONALD’S CORPORATION,
          MCDONALD’S USA, AND CORPORATE-OWNED RESTAURANTS ONLY:
        </b>
      </h3>
      <ol>
        <TermsAndConditionsContentListItem>
          Access to the OTP Mobile App is authorized only for the individual
          associated with each applicable username and password. The OTP Mobile
          App may only be used while you are and remain employed by McDonald’s
          or as otherwise authorized in writing by McDonald’s. If your
          employment with McDonald’s ends, your access to the OTP Mobile App
          will terminate.
        </TermsAndConditionsContentListItem>
        <TermsAndConditionsContentListItem>
          The OTP Mobile App may not be used by you for any purpose that is not
          authorized by and/or consistent with these Terms of Use.
        </TermsAndConditionsContentListItem>
        <TermsAndConditionsContentListItem>
          McDonald’s may monitor and record your use of the OTP Mobile App,
          including activity you conduct while using the app, to operate the OTP
          Mobile App, to evaluate your use thereof, for compliance and audit
          purposes, and to protect against fraud, illegal activity, violation of
          McDonald’s policies, or misuse of the OTP Mobile App or McDonald’s
          information, assets, or other property. Accordingly, you should not
          have any expectation of privacy in connection with your use of the OTP
          Mobile App.
        </TermsAndConditionsContentListItem>
        <TermsAndConditionsContentListItem>
          McDonald’s may restrict, suspend, or terminate your access to the OTP
          Mobile App for any reason not prohibited by law. Failure to comply
          with these Terms of Use, policies applicable to your employment, and
          applicable laws may subject you to discipline, up to and including
          termination from employment, and may also result in the restriction,
          suspension, or termination of your access to the OTP Mobile App. You
          understand and agree that McDonald’s and its subsidiaries and/or
          affiliates may take any one or more of these actions without providing
          notice to you, except as prohibited by law.
        </TermsAndConditionsContentListItem>
        <TermsAndConditionsContentListItem>
          The OTP Mobile App is intended to serve as a convenient tool to
          facilitate your work. For your benefit and convenience, you may access
          the OTP Mobile App from anywhere to facilitate your day-to-day tasks
          while clocked in. For hourly, non-salaried employees, using the OTP
          Mobile App to perform off-the-clock work when you are NOT clocked in
          is a violation of McDonald’s policy for which you can be disciplined,
          up to and including termination. If you use the OTP Mobile App while
          not clocked in, you must immediately report the amount of time you
          spent using the site to the McOpCo Human Resources Manager so that
          McDonald’s can pay you. Your failure to comply with wage-hour
          policies, including the prohibition against performing work while not
          clocked in, may result in discipline, up to and including termination
          from employment.
        </TermsAndConditionsContentListItem>
        <TermsAndConditionsContentListItem>
          McDonald’s and/or its affiliates are not liable for any damages or
          injury arising out of or related to the OTP Mobile App, however
          caused, including, but not limited to, any failure of performance,
          error, omission, interruption, delay, or defect caused as a direct or
          indirect result of you using the OTP Mobile App.
        </TermsAndConditionsContentListItem>
        <TermsAndConditionsContentListItem>
          Any communication or material you transmit through the OTP Mobile App,
          including any data, posts, comments, remarks, suggestions, ideas,
          graphics, photographs, materials, or other information (collectively,
          the “<b>Submissions</b>”) is, and will be treated as, non-confidential
          and non-proprietary. McDonald’s may contact you about any such
          Submissions for business- related purposes. Furthermore, you grant
          McDonald’s the perpetual, irrevocable, royalty-free, nonexclusive, and
          worldwide right to use the Submission(s) for any purpose whatsoever,
          commercial or otherwise, including, but not limited to, developing,
          manufacturing, and marketing products or services, or conducting data
          analytics, using such Submissions, without your further approval and
          without providing you any further notice or compensation. By making
          any Submissions, you represent that you have all requisite rights to,
          and are authorized to disclose, all of the information contained in
          the Submissions.
        </TermsAndConditionsContentListItem>
        <TermsAndConditionsContentListItem>
          Any and all rights in the OTP Mobile App are and shall remain the
          exclusive property of McDonald’s or its licensors. For purposes of
          clarity, the OTP Mobile App includes any and all content on the OTP
          Mobile App, such as, but not limited to, text, images, graphics,
          logos, page headers, button icons, images, audio clips, digital
          downloads, data compilations, software, trademarks, service marks,
          trade dress, audio, video, data and other materials as well as any
          part of the OTP Mobile App. The OTP Mobile App is licensed, not sold,
          to you. Nothing in these terms intends to transfer any such rights to,
          or to vest any such right in, you. You may not take any action to
          jeopardize, limit or interfere with McDonald’s or its licensor’s
          rights.
        </TermsAndConditionsContentListItem>
        <TermsAndConditionsContentListItem>
          TTrademarks, service marks, and all graphical elements, including the
          look and feel appearing in the OTP Mobile App, are distinctive and
          protected trademarks or trade dress of McDonald’s or its licensors.
          The OTP Mobile App may also contain various third-party names,
          trademarks, and service marks that are the property of their
          respective owners.
        </TermsAndConditionsContentListItem>
        <TermsAndConditionsContentListItem>
          Any questions regarding the OTP Mobile App should be directed to Atos
          at (800) 515-3636
        </TermsAndConditionsContentListItem>
        <TermsAndConditionsContentListItem>
          Any exclusion or restriction of liability contained in these Terms of
          Use shall apply to the fullest extent permitted by applicable law.
        </TermsAndConditionsContentListItem>
        <TermsAndConditionsContentListItem>
          Any use of the OTP Mobile App shall be subject to the applicable laws
          of the State of Illinois and any dispute arising from such use or
          otherwise in connection with the OTP Mobile App shall be subject to
          the exclusive jurisdiction of the federal or state courts in the State
          of Illinois, as applicable.
        </TermsAndConditionsContentListItem>
      </ol>
    </div>
  );
};
